import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import workOrdersService from '../../services/workOrdersService';
import WorkOrderDetailsModal from './WorkOrderDetailsModal';
import './PrintQueueView.css';

const PRIORITY_CONFIG = {
  rush: { color: '#ef4444', bg: '#fef2f2', label: 'Rush' },
  high: { color: '#f59e0b', bg: '#fffbeb', label: 'High' },
  normal: { color: '#3b82f6', bg: '#eff6ff', label: 'Normal' },
  low: { color: '#6b7280', bg: '#f9fafb', label: 'Low' }
};

const WORK_ORDER_STATUSES = ['Draft', 'Submitted', 'In Production', 'Completed', 'Delivered'];

const PrintQueueView = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const fetchPrintQueue = useCallback(async () => {
    try {
      setLoading(true);
      const response = await workOrdersService.getPrintQueue();
      setJobs(response.jobs || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching print queue:', err);
      setError(err.response?.data?.message || 'Failed to fetch print queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrintQueue();
  }, [fetchPrintQueue]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const isOverdue = (job) => {
    if (!job.due_date) return false;
    return new Date(job.due_date) < new Date();
  };

  const isDueSoon = (job) => {
    if (!job.due_date) return false;
    const dueDate = new Date(job.due_date);
    const today = new Date();
    const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 3;
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setIsDetailsModalOpen(true);
  };

  const handleModalSuccess = () => {
    fetchPrintQueue();
  };

  const renderPriorityBadge = (priority) => {
    const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
    return (
      <span
        className="pq-priority-badge"
        style={{
          backgroundColor: config.bg,
          color: config.color,
          borderColor: config.color
        }}
      >
        {config.label}
      </span>
    );
  };

  // Group by due date
  const groupedJobs = jobs.reduce((groups, job) => {
    const dateKey = job.due_date ? new Date(job.due_date).toDateString() : 'No Due Date';
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(job);
    return groups;
  }, {});

  // Sort date keys
  const sortedDateKeys = Object.keys(groupedJobs).sort((a, b) => {
    if (a === 'No Due Date') return 1;
    if (b === 'No Due Date') return -1;
    return new Date(a) - new Date(b);
  });

  return (
    <div className="pq-container">
      {/* Header */}
      <div className="pq-header">
        <div className="pq-header-content">
          <div className="pq-title-section">
            <Link to="/work-orders" className="pq-back-link">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </Link>
            <div className="pq-page-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 6 2 18 2 18 9"/>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                <rect x="6" y="14" width="12" height="8"/>
              </svg>
            </div>
            <div>
              <h1>Print Queue</h1>
              <p>Work orders in production, sorted by due date</p>
            </div>
          </div>
          <div className="pq-stats">
            <div className="pq-stat">
              <span className="pq-stat-value">{jobs.length}</span>
              <span className="pq-stat-label">In Queue</span>
            </div>
            <div className="pq-stat pq-stat-overdue">
              <span className="pq-stat-value">{jobs.filter(isOverdue).length}</span>
              <span className="pq-stat-label">Overdue</span>
            </div>
            <div className="pq-stat pq-stat-rush">
              <span className="pq-stat-value">{jobs.filter(j => j.priority === 'rush').length}</span>
              <span className="pq-stat-label">Rush</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pq-main">
        {loading ? (
          <div className="pq-loading">
            <div className="pq-spinner" />
            <p>Loading print queue...</p>
          </div>
        ) : error ? (
          <div className="pq-error">
            <h3>Error</h3>
            <p>{error}</p>
            <button onClick={fetchPrintQueue}>Try Again</button>
          </div>
        ) : jobs.length === 0 ? (
          <div className="pq-empty">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            <h3>Print Queue Empty</h3>
            <p>No work orders are currently in production</p>
            <Link to="/work-orders" className="pq-back-btn">
              Back to Work Orders
            </Link>
          </div>
        ) : (
          <div className="pq-queue-list">
            {sortedDateKeys.map(dateKey => {
              const dateJobs = groupedJobs[dateKey];
              const isOverdueDate = dateKey !== 'No Due Date' && new Date(dateKey) < new Date();
              const isTodayDate = dateKey !== 'No Due Date' && new Date(dateKey).toDateString() === new Date().toDateString();

              return (
                <div key={dateKey} className="pq-date-group">
                  <div className={`pq-date-header ${isOverdueDate ? 'overdue' : ''} ${isTodayDate ? 'today' : ''}`}>
                    <span className="pq-date-label">
                      {dateKey === 'No Due Date' ? dateKey : formatDate(dateKey)}
                      {isOverdueDate && <span className="pq-overdue-badge">OVERDUE</span>}
                      {isTodayDate && <span className="pq-today-badge">TODAY</span>}
                    </span>
                    <span className="pq-date-count">{dateJobs.length} order{dateJobs.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="pq-jobs-list">
                    {dateJobs.map(job => (
                      <div
                        key={job.id}
                        className={`pq-job-card ${job.priority === 'rush' ? 'rush' : ''} ${isOverdue(job) ? 'overdue' : ''}`}
                        onClick={() => handleJobClick(job)}
                      >
                        <div className="pq-job-priority-bar" style={{ backgroundColor: PRIORITY_CONFIG[job.priority]?.color || '#3b82f6' }} />
                        <div className="pq-job-content">
                          <div className="pq-job-header">
                            <span className="pq-job-number">{job.work_order_number}</span>
                            {renderPriorityBadge(job.priority)}
                          </div>
                          <div className="pq-job-customer">{job.customer}</div>
                          <div className="pq-job-details">
                            {job.invoice_number && (
                              <span className="pq-job-invoice">Invoice: {job.invoice_number}</span>
                            )}
                            {job.print_by && (
                              <span className="pq-job-printer">Printer: {job.print_by}</span>
                            )}
                          </div>
                          {job.special_instructions && (
                            <div className="pq-job-instructions">
                              {job.special_instructions.length > 100
                                ? job.special_instructions.substring(0, 100) + '...'
                                : job.special_instructions}
                            </div>
                          )}
                        </div>
                        <div className="pq-job-actions">
                          <button
                            className="pq-btn-pdf"
                            onClick={(e) => {
                              e.stopPropagation();
                              workOrdersService.downloadPdf(job.id, job.work_order_number);
                            }}
                            title="Download PDF"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                              <polyline points="14 2 14 8 20 8"/>
                              <line x1="12" y1="18" x2="12" y2="12"/>
                              <polyline points="9 15 12 18 15 15"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Details Modal */}
      <WorkOrderDetailsModal
        job={selectedJob}
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedJob(null);
        }}
        onSuccess={handleModalSuccess}
        onEdit={() => {}}
        canAdmin={true}
        statuses={WORK_ORDER_STATUSES}
      />
    </div>
  );
};

export default PrintQueueView;
